/**
 * Storage de Hospedagens (admin).
 * Persistido em localStorage.
 * Inclui suporte a iCal (Booking/Airbnb) para evitar overbooking.
 */

export type TipoHospedagem =
  | 'hotel'
  | 'pousada'
  | 'hostel'
  | 'chale'
  | 'camping'
  | 'apartamento'
  | 'casa'
  | 'resort'
  | 'outro';

/** Comodidades típicas de hospedagem */
export const COMODIDADES_LIST: { id: string; label: string }[] = [
  { id: 'wifi', label: 'Wi-Fi' },
  { id: 'ar_condicionado', label: 'Ar-condicionado' },
  { id: 'piscina', label: 'Piscina' },
  { id: 'cafe_manha', label: 'Café da manhã' },
  { id: 'pet_friendly', label: 'Pet friendly' },
  { id: 'estacionamento', label: 'Estacionamento' },
  { id: 'restaurante', label: 'Restaurante' },
  { id: 'bar', label: 'Bar' },
  { id: 'academia', label: 'Academia' },
  { id: 'spa', label: 'Spa' },
  { id: 'tv', label: 'TV' },
  { id: 'frigobar', label: 'Frigobar' },
  { id: 'cozinha', label: 'Cozinha' },
  { id: 'lavanderia', label: 'Lavanderia' },
  { id: 'transfer', label: 'Transfer aeroporto' },
  { id: 'varanda', label: 'Varanda' },
  { id: 'vista_mar', label: 'Vista para o mar' },
  { id: 'acessibilidade', label: 'Acessibilidade' },
];

export type StatusPadraoHospedagem = 'ativado' | 'desativado';

/** Destaque na listagem (mesma semântica que notícias / estabelecimentos). */
export type HospedagemLayoutType = 'normal' | 'matéria_principal' | 'slide_principal';

export type HospedagemSource = 'manual' | 'google_import';

/** Tipo de quarto com quantidade e capacidade */
export interface TipoQuarto {
  /** Nome do tipo (ex: Duplo, Triplo, Suíte) */
  tipo: string;
  /** Quantidade de quartos deste tipo */
  quantidade: number;
  /** Capacidade em pessoas por quarto */
  capacidade: number;
}

/** Uma URL iCal ( Booking ou Airbnb ) - suporta múltiplas por quarto/listing */
export interface ICalUrlItem {
  /** URL do calendário */
  url: string;
  /** Origem: booking ou airbnb */
  origem: 'booking' | 'airbnb';
  /** Descrição opcional (ex: Quarto Duplo, Suíte) */
  descricao?: string;
}

export interface ICalConfig {
  /** Lista de URLs iCal - cada quarto/listing pode ter a sua */
  icalUrls: ICalUrlItem[];
  /** Última sincronização (ISO) */
  lastSyncAt?: string;
  /** Datas bloqueadas vindas dos iCal (YYYY-MM-DD) - combinadas de todas as URLs */
  blockedDatesFromIcal: string[];
}

export interface Hospedagem {
  id: string;
  nome: string;
  descricao?: string;
  tipo: TipoHospedagem;
  cityId?: string;
  endereco?: string;
  lat?: number;
  lng?: number;
  /** URLs das fotos */
  fotos: string[];
  comodidades: string[];
  /** Tipos de quarto com capacidades diferentes (substitui numQuartos/capacidadeHospedes) */
  quartos: TipoQuarto[];
  /** Horário padrão check-in (ex: "14:00") */
  checkIn?: string;
  /** Horário padrão check-out (ex: "11:00") */
  checkOut?: string;
  politicas?: string;
  /** Estrelas/categoria (1-5) */
  estrelas?: number;
  /** Bloqueios manuais por data (YYYY-MM-DD) - ex: manutenção */
  bloqueiosManuais: Record<string, { motivo?: string }>;
  /** Configuração iCal para sync com Booking/Airbnb */
  icalConfig?: ICalConfig;
  statusPadrao: StatusPadraoHospedagem;
  periodosStatus?: {
    de: string;
    ate: string;
    tipo: 'ativado' | 'desativado' | 'naviex' | 'principal' | 'categoria';
  }[];
  layoutType?: HospedagemLayoutType;
  layoutExpiresAt?: string;
  destaqueSubcategoria?: boolean;
  destaqueSubcategoriaExpiresAt?: string;
  destaqueNaviex?: boolean;
  destaqueNaviexExpiresAt?: string;
  source?: HospedagemSource;
  googlePlaceId?: string;
  rating?: number;
  totalRatings?: number;
  formattedAddress?: string;
  /** Admin: rascunho até publicar; omitido = publicado (legado). */
  publicacaoStatus?: 'rascunho' | 'publicado';
  createdAt: string;
  updatedAt: string;
}

const KEY_HOSPEDAGENS = 'naviex-admin-hospedagens';

function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function saveJson(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

/** Migra formato antigo (bookingUrl/airbnbUrl, numQuartos/capacidadeHospedes) para novo */
function normalizeHospedagem(h: Hospedagem & { numQuartos?: number; capacidadeHospedes?: number }): Hospedagem {
  const ical = h.icalConfig as { bookingUrl?: string; airbnbUrl?: string; icalUrls?: ICalUrlItem[]; lastSyncAt?: string; blockedDatesFromIcal?: string[] } | undefined;
  const icalUrls: ICalUrlItem[] = [...(ical?.icalUrls ?? [])];
  if (icalUrls.length === 0 && (ical?.bookingUrl || ical?.airbnbUrl)) {
    if (ical.bookingUrl?.trim()) icalUrls.push({ url: ical.bookingUrl.trim(), origem: 'booking', descricao: 'Booking.com' });
    if (ical.airbnbUrl?.trim()) icalUrls.push({ url: ical.airbnbUrl.trim(), origem: 'airbnb', descricao: 'Airbnb' });
  }
  const quartos = h.quartos ?? [];
  const hasOldCap = h.numQuartos != null || h.capacidadeHospedes != null;
  const nextQuartos = quartos.length > 0 || !hasOldCap
    ? quartos
    : [{ tipo: 'Padrão', quantidade: h.numQuartos ?? 1, capacidade: h.capacidadeHospedes ?? 2 }];
  const { numQuartos: _nq, capacidadeHospedes: _ch, ...rest } = h;
  return {
    ...rest,
    quartos: nextQuartos,
    icalConfig: {
      icalUrls,
      lastSyncAt: ical?.lastSyncAt,
      blockedDatesFromIcal: ical?.blockedDatesFromIcal ?? [],
    },
  };
}

export function getHospedagens(): Hospedagem[] {
  return loadJson<Hospedagem[]>(KEY_HOSPEDAGENS, []).map(normalizeHospedagem);
}

export function getHospedagemById(id: string): Hospedagem | null {
  return getHospedagens().find((h) => h.id === id) ?? null;
}

export function addHospedagem(data: Omit<Hospedagem, 'id' | 'createdAt' | 'updatedAt'>): Hospedagem {
  const now = new Date().toISOString();
  const list = getHospedagens();
  const ical = data.icalConfig ?? { blockedDatesFromIcal: [] };
  const icalNorm = {
    ...ical,
    icalUrls: ical.icalUrls ?? [],
    blockedDatesFromIcal: ical.blockedDatesFromIcal ?? [],
  };
  const next: Hospedagem = {
    ...data,
    id: `hosp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    fotos: data.fotos ?? [],
    comodidades: data.comodidades ?? [],
    quartos: data.quartos ?? [],
    bloqueiosManuais: data.bloqueiosManuais ?? {},
    icalConfig: icalNorm,
    createdAt: now,
    updatedAt: now,
  };
  saveJson(KEY_HOSPEDAGENS, [...list, next]);
  return next;
}

export function saveHospedagem(hosp: Hospedagem): void {
  const list = getHospedagens();
  const idx = list.findIndex((h) => h.id === hosp.id);
  if (idx < 0) throw new Error('Hospedagem não encontrada.');
  const updated = { ...hosp, updatedAt: new Date().toISOString() };
  const next = [...list];
  next[idx] = updated;
  saveJson(KEY_HOSPEDAGENS, next);
}

export function removeHospedagem(id: string): void {
  saveJson(
    KEY_HOSPEDAGENS,
    getHospedagens().filter((h) => h.id !== id)
  );
}

export function isHospedagemAtiva(hosp: Hospedagem): boolean {
  const hoje = new Date().toISOString().slice(0, 10);
  const periodos = hosp.periodosStatus ?? [];
  const padrao = hosp.statusPadrao ?? 'ativado';
  for (const p of periodos) {
    if (hoje >= p.de && hoje <= p.ate) {
      return p.tipo !== 'desativado';
    }
  }
  return padrao === 'ativado';
}

/**
 * Retorna todas as datas bloqueadas (manuais + iCal).
 * Útil para exibir calendário de disponibilidade.
 */
export function getDatasBloqueadas(hosp: Hospedagem): Set<string> {
  const set = new Set<string>();
  for (const d of Object.keys(hosp.bloqueiosManuais ?? {})) {
    set.add(d);
  }
  const ical = hosp.icalConfig?.blockedDatesFromIcal ?? [];
  ical.forEach((d) => set.add(d));
  return set;
}

export const TIPOS_HOSPEDAGEM: { value: TipoHospedagem; label: string }[] = [
  { value: 'hotel', label: 'Hotel' },
  { value: 'pousada', label: 'Pousada' },
  { value: 'hostel', label: 'Hostel' },
  { value: 'chale', label: 'Chalé' },
  { value: 'camping', label: 'Camping' },
  { value: 'apartamento', label: 'Apartamento' },
  { value: 'casa', label: 'Casa' },
  { value: 'resort', label: 'Resort' },
  { value: 'outro', label: 'Outro' },
];
